import Backbone from 'backbone'
import template from './dataset-connection-row.hbs'
import importDataSourceInfo from 'scoreboard/admin/imports/import-datasource-info.hbs'
import $ from 'jquery'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import momentHelper from 'scoreboard/components/moment-helper'
import guard from 'scoreboard/components/unsaved/guard'
import DatasetsTransformations from './edit/datasets-transformations'
import DataSourceInfoWizard from 'scoreboard/imports/data-source-info-wizard'
import LabelOwnersSchedule, {SCHEDULE} from 'scoreboard/imports/label-owners-schedule'
import appDisplay from 'scoreboard/components/app-display'
import conductor from 'scoreboard/components/conductor'
import Dataset from './model/dataset'
import {downloadFromUrl} from 'scoreboard/components/files/file'
import RefreshButton from 'scoreboard/components/refresh-button'
import datasetStatus from 'scoreboard/datasets/not-available/dataset-status'
import FetchNewDataWizard from './fetch-new-data-wizard'
import UploadSpreadsheetWizard from 'scoreboard/imports/upload-spreadsheet-wizard'
import disableControls from 'scoreboard/components/disable-controls'
import constants from 'scoreboard/components/constants'
import { sendReloadRequest } from 'scoreboard/datasets/fetch-new-data-form'

const { IMPORT_DATA_SOURCE_TYPE } = constants
const { STALE, LOADING, STALE_METADATA } = Dataset
const FETCH_NEW_DATA = 'fetch-new-data'
const REBUILD_DATASET = 'rebuild-dataset'

const DatasetConnectionRow = Backbone.View.extend({

	/**
	 * Renders the connection information for a dataset (#24205). Since there is
	 * only one connection per dataset, the connection will be read directly
	 * from the model.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model
	 *
	 * @param {Boolean} [options.autoRender=true] indicates this view should
	 * automatically re-render itself when changes are made. If set to false,
	 * a 'render' event is triggered instead
	 */
	initialize: function(options = {}) {
		this.options = options
		this.autoRender = _.isNil(this.options.autoRender) ? true : this.options.autoRender
	},

	events: {
		'click .data-source-info .edit': 'onEditDataSourceInfo',
		'click .fetch-new-data-button': 'fetchNewData',
		'click .rebuild-dataset-button': 'rebuildDataset',
		'click .upload': 'uploadSpreadsheet',
		'click .download': 'downloadSpreadsheet',
		'click .transformations .edit': 'onEditTransformations',
		'click .schedule .edit': 'onEditSchedule',
		'click .explore': 'onExplore',
		'click .main-actions .edit': 'onEdit'
	},

	tagName: 'li',
	className: 'import-connection-row item display-only',

	render: function() {
		const context = this.context()
		if (this.rendered && !this.autoRender) {
			// Undelegate events if we've already rendered. Events are rebinded
			// when the new markup is rendered.
			this.undelegateEvents()
			this.trigger('render', this.model)
			this.$el.find('.data-source-info')
				.empty()
				.append(importDataSourceInfo(context))
			// #53922 hide REST import link
			if (context.dataSourceMetadata.source == 'rest') {
				this.$('.arrow, a.text-link.download').addClass('is-not-displayed')
			}
			return this
		}
		// get the appropriate status value from import-connection-row.scss
		// which matches the names from ImportStatus.java
		let status
		switch (this.model.get('status')) {
			case 'error':
				status = 'failed'; break
			case 'ready':
			case 'indexed':
				status = 'success'; break
		}
		this.$el.html(template(context))
			.data('id', this.model.id)
			.addClass(status)
			.find('.data-source-info')
			.append(importDataSourceInfo(context))

		// display the Fetch New Data button if it should be visible
		this.hideFetchNewDataButton(false)
		this.initDatasetStatusListener()
		this.hideConnectionDetailsForEmptyDataSource()
		this.disableEditingForEmptyDataSourceWithSupplementalRecords()
		// #35626 hide the download button if needed (ex. Google Sheets)
		this.hideDownloadButton()

		this.rendered = true
		return this
	},

	context: function() {
		let metadata = this.model.get('dataSourceMetadata')

		let schedule = _.get(this.model.attributes, 'scheduleData.description') ||
			i18n.get('admin.import.scheduled.not')
		let importBehavior = undefined
		let appendRecordCode = 'admin.import.spreadsheet.append.schedule'
		if (_.isNil(this.model.get('primaryKeyFieldId'))) {
			appendRecordCode += '.short'
		}
		// #56364 set the importBehavior message code only if replaceAllRecords
		// is set AND scheduleData is not empty (i.e. the dataset is scheduled)
		if (!_.isNil(this.model.get('replaceAllRecords'))
			&& !_.isEmpty(this.model.attributes?.scheduleData)) {
			importBehavior = this.model.get('replaceAllRecords') ?
				'admin.import.spreadsheet.replace.schedule' :
				appendRecordCode
		}
		const lastImportTime = this.getLastImportTime()

		let icon = this.model.isSpreadsheet() ? 'docs-spreadsheet-mono' : 'database'
		if (metadata.source === 'rest') {
			icon = 'webservice'
		}
		const connection = metadata.importConnection

		// #47412
		let label = metadata.label || connection?.label
		let fetchNewDataDescription = i18n.get('datasets.dataSource.fetchNewData.description', label)
		const zapierInfoMessage = i18n.get('datasets.dataSource.fetchNewData.zapierSuffix')
		if (metadata.source === 'zapier') { // ImportDataSourceType
			fetchNewDataDescription += `<br><br>${zapierInfoMessage}`
		}

		this.populateSheetName(metadata)

		return _.extend({}, metadata, {
			id: this.model.id,
			dataSourceMetadata: metadata,
			connection,
			fileUpload: this.model.isFileUpload(),
			datasetLabel: this.model.get('label'),
			icon,
			numTransformations: _.size(this.model.get('transformations')),
			schedule,
			lastImportTime,
			isSpreadsheet: this.model.isSpreadsheet(),
			isDatabase: metadata.source === 'database',
			FETCH_NEW_DATA,
			UPLOAD: IMPORT_DATA_SOURCE_TYPE.UPLOAD,
			REBUILD_DATASET,
			importBehavior: importBehavior,
			fetchNewDataDescription,
			resultsMessage: this.getResultsMessage()
		})
	},

	/**
	 * For datasets using a spreadsheet datasource, populates metadata.sheetName
	 * based on metadata.sheets and metadata.sheetIndex.
	 * If populated, the sheet name will display on import-datasource-info.hbs
	 * #51781
	 */
	populateSheetName: function(metadata) {
		if (this.model.isSpreadsheet()) {
			let sheets = _.get(metadata, 'sheets', [])
			let sheetIndex = _.get(metadata, 'sheetIndex', 0)
			let sheetName = _.nth(sheets, sheetIndex)
			metadata.sheetName = sheetName
		}
	},

	getLastImportTime: function() {
		let metadata = this.model.get('dataSourceMetadata')
		let lastImportTime = _.get(metadata, 'lastImportTime')
		return lastImportTime ?
			momentHelper.formatDateTimeWithZone(lastImportTime) :
			i18n.get('alerts.never')
	},

	getResultsMessage: function() {
		const metadata = this.model.get('dataSourceMetadata')
		if (metadata && metadata.source !== IMPORT_DATA_SOURCE_TYPE.ZAPIER) return

		if (metadata.numRows == 0 || !!metadata.lastFileEmpty) {
			return i18n.get('admin.import.errors.noData')
		}
	},

	updateLastImportTime: function() {
		this.$('.last-import-time').empty().text(this.getLastImportTime())
	},

	updateLastFileEmpty: function() {
		this.$('.results-message').empty().text(this.getResultsMessage())
	},

	onEditDataSourceInfo: function(e) {
		guard.conditionallyRun(() => {
			let url = `api/datasets/${this.model.id}/data-source-info`
			let self = this
			appDisplay.dialog($.get(url), ({ connections, dataset, currentConnection }) => {
				let newModel = new Dataset(dataset)
				let wizard = new DataSourceInfoWizard({
					model: newModel,
					connections,
					currentConnection,
					accept: 'spreadsheetOrZip',
					useDatasetInstructions: true,
					showReplaceAllRecordOptions: true,
					doneKey: 'common.save',
					formatValues: false
				})
				wizard.on('done', (model) => {
					// #31122 this action is separate of the normal edit functionality
					// of a dataset, so we need to manually update the model so
					// the dataset source info updates correctly
					self.model.set(model.toJSON())
					this.render()
					this.trigger('change', this.model)
				})
				return wizard._view()
			})
		})
	},

	uploadSpreadsheet: function(e) {
		guard.conditionallyRun(() => {
			const uploadSpreadsheetWizard = new UploadSpreadsheetWizard({
				model: this.model,
				rebuild: false,
				doneKey: 'admin.import',
				url: `api/datasets/${this.model.id}/spreadsheets/columns`
			}).start()
			uploadSpreadsheetWizard.on('done', model => {
				this.model = model
				this.render()
				this.trigger('change', this.model)
			}).bind(this)
		})
	},

	fetchNewData: function(e) {
		guard.conditionallyRun(() => {
				const fetchNewDataWizard = new FetchNewDataWizard({
					model: this.model,
					doneKey: 'admin.import'
				})
				fetchNewDataWizard.on('done', model => {
					this.model = model
					this.render()
				})
				fetchNewDataWizard.start()
		})
	},

	rebuildDataset: function() {
		guard.conditionallyRun(() => {
			// #35409 Normally the status is set by a signal from the server, but
			// we do go ahead and set it to loading here so the icon starts animating
			// right away
			this.updateRefreshButtons(Dataset.LOADING)
			sendReloadRequest({
				dataset: this.model,
				params: {
					fetchNewData: false,
					replaceAllRecords: false,
					rebuildCachedMetadata: true
				}
			})
		})
	},

	// Show the Fetch New Data button when we're dealing with a database/FTP/SFTP
	// sourced dataset
	shouldShowFetchNewDataButton: function() {
		return !this.model.hasNoDataSource() && !this.model.isFileUpload() &&
			!this.model.isManagedDocument()
	},

	/**
	 * @param button FETCH_NEW_DATA or REBUILD_DATASET
	 */
	getRefreshButton: function(button) {
		return this.$(`.button[data-button="${button}"]`)
	},

	initDatasetStatusListener: function() {
		const status = this.model.get('status')

		// update the refresh button's state on initial and subsequent re-renders.
		this.updateRefreshButtons(status)

		datasetStatus().then(status => {
			this.listenTo(status, 'change', props => {
				if (props.id !== this.model.id) {
					return
				}

				const metadata = this.model.get('dataSourceMetadata')

				// #37619 update the Last Run time if it was sent through the websocket
				if (props.lastImportTime) {
					metadata.lastImportTime = props.lastImportTime
					this.model.set('dataSourceMetadata', metadata)
					this.updateLastImportTime()
				}

				// update the model's status and error properties so we don't have
				// a stale model once the view re-renders
				this.model.set('status', props.status)
				if (props.errorCode) {
					this.model.set('errorCode', props.errorCode)
					this.model.set('errorArguments', props.errorArguments)
				}

				if (!_.isNil(props.numRows)) {
					metadata.numRows = props.numRows
					this.model.set('dataSourceMetadata', metadata)
					this.render()
				}

				// #49329 update if the last file was empty if that value was sent through the websocket
				if (!_.isNil(props.lastFileEmpty)) {
					metadata.lastFileEmpty = props.lastFileEmpty
					this.updateLastFileEmpty()
				}

				this.updateRefreshButtons(props.status)
				this.trigger('resetTooltips')
			})
		})
	},

	updateRefreshButtons: function(status) {
		if ([STALE, LOADING, STALE_METADATA].includes(status)) {
			this.startRefreshButton(REBUILD_DATASET)
			// Because both buttons perform almost the same action, they should both
			// be sensitive to the current status of the dataset. If the dataset is
			// being processed, we will end up showing 2 buttons with the loading
			// animation, which looks weird. So we simply hide one during this time
			this.hideFetchNewDataButton(true)
		} else {
			this.stopRefreshButton(REBUILD_DATASET)
			this.hideFetchNewDataButton(false)
		}
	},

	startRefreshButton: function(button) {
		if (!this[button]) {
			const buttonEl = this.getRefreshButton(button)
			buttonEl.addClass('refresh-button busy-button')
			this[button] = new RefreshButton({el: buttonEl})
		}
		if (this[button].state !== 'loading') {
			this[button].loading()
		}
	},

	stopRefreshButton: function(button) {
		if (!this[button]) {
			return
		}
		// #35410 Copied this from imports.js. Seems that if the "refresh" processes
		// too quickly, it doesn't give the checkmark animation enough time to
		// complete
		const buttonEl = this[button]
		setTimeout(() => buttonEl.done(), 100)

	},

	hideFetchNewDataButton(hide) {
		this.getRefreshButton(FETCH_NEW_DATA).toggleClass('is-not-displayed',
			hide || !this.shouldShowFetchNewDataButton())
	},

	hideConnectionDetailsForEmptyDataSource: function() {
		if (!this.model.hasNoDataSource()) return

		// hide the Transformations, Import Schedule, and Last Run details
		this.$('.transformations, .schedule, .last-run').addClass('is-not-displayed')

		// hide the Fetch New Data button
		this.getRefreshButton(FETCH_NEW_DATA).addClass('is-not-displayed')
	},

	hideDownloadButton: function() {
		// #35626 hide the download button for Google Sheets
		if (this.model.isGoogleSheet()) {
			this.$('.download').addClass('is-not-displayed')
		}
	},

	disableEditingForEmptyDataSourceWithSupplementalRecords: function() {
		if (!this.model.hasNoDataSource()) return
		if (!this.model.get('hasSupplementalRecords')) return

		this.editDisabledTooltip && this.editDisabledTooltip.remove()

		const editButton = this.$('[name=dataSourceEdit]')
			.addClass('is-disabled')
			.attr('data-action', i18n.get('datasets.dataSource.none.cantBeEdited'))
			.on('click', e => e.stopPropagation())
		disableControls(editButton)
	},

	downloadSpreadsheet: function(e) {
		var metadata = this.model.attributes.dataSourceMetadata
		downloadFromUrl('datasets/' + this.model.id + '/spreadsheets/' + metadata.id)
	},

	onEditTransformations: function() {
		guard.conditionallyRun(() => {
			// #32392 since transformations.js mutates the transformations
			// on the model that is passed in, use a copy of this.model
			// so we don't have changes sticking around if dialog is cancelled
			let model = _.cloneDeep(this.model)
			// #40586 format the preview of transformed values when editing
			// transformations for an existing dataset
			let transformations = new DatasetsTransformations({
				model,
				formatValues: false // #50190 display unformatted data
			})
			transformations.on('done', (model) => {
				// set the transformations on this.model
				this.model.set('transformations',
					_.get(model.attributes, 'transformations'), [])
				this.render()
			})
			appDisplay.dialog(transformations)

		})
	},

	onEditSchedule: function() {
		guard.conditionallyRun(() => {
			let scheduleForm = new LabelOwnersSchedule({
				model: this.model,
				mode: SCHEDULE
			})
			scheduleForm.on('done', () => this.render())
			appDisplay.dialog(scheduleForm)
		})
	},

	onExplore: function() {
		conductor.overview('datasets', this.model.id)
	},

	onEdit: function(e) {
		// #28812 don't trigger ScheduledImports.edit
		e && e.stopPropagation()
		conductor.edit('datasets', this.model.id)
	}

})

export default DatasetConnectionRow

DatasetConnectionRow.__filename__ = 'datasets/dataset-connection-row.js'
